import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';

import { Button, Dialog, useDialog, DialogActions } from 'ui-kit';

import { FileState } from '__generated__/globalTypes';

import { FieldsMap } from './fieldMapping/MapRequiredFieldsCard';
import { useMapHeadersToFields } from './hooks/useMapHeadersToFields';
import { InProgressOverlay } from './InProgressOverlay';
import { useTapeUploadContext } from './loanTapeUploader/TapeUploadContext';

interface MapFieldsOperationContextProps {
  startMapFields: (fileId: string, fieldMap: FieldsMap) => void;
}

const MapFieldsOperationContext = createContext<MapFieldsOperationContextProps | null>(null);

interface MapFieldsOperationWrapperProps {
  fileState: FileState;
  isStarted: boolean;
  setIsStarted: React.Dispatch<React.SetStateAction<boolean>>;
  processingPercent?: number;
}

/*
 * HACK (kentskinner)
 * Emergency fix for RC-1. Delete either this or MapFieldsOperationProvider soon.
 */
export const MapFieldsOperationWrapper: React.FC<MapFieldsOperationWrapperProps> = ({
  fileState,
  isStarted,
  setIsStarted,
  processingPercent,
}) => {
  const [progress, setProgress] = useState<number>(0);
  const [description, setDescription] = useState<string>('Initializing...');
  const [subDescription, setSubDescription] = useState<string | undefined>(undefined);
  const [buttonText, setButtonText] = useState<string>('Cancel');
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('An error occurred');

  useEffect(() => {
    switch (fileState) {
      case FileState.NEEDS_MAPPING:
        setProgress(0);
        setDescription('Starting...');
        setSubDescription(undefined);
        setButtonText('Cancel');
        break;

      case FileState.READY_TO_PROCESS:
        setProgress(0);
        setDescription('Mapping fields');
        setSubDescription(undefined);
        setButtonText('Cancel');
        break;

      case FileState.PROCESSING:
        const percent = processingPercent ?? 0;
        setProgress(percent);
        setDescription('Processing loans...');
        setSubDescription('You can close this and come back later.');
        setButtonText('OK');
        break;

      case FileState.PROCESSED:
        setProgress(100);
        setDescription('Fields mapped.');
        setSubDescription(undefined);
        setButtonText('OK');
        setIsStarted(false);
        break;

      case FileState.ERROR:
        setErrorMessage('An error occurred while mapping fields');
        setIsError(true);
        break;

      case FileState.ERROR_PASSWORD:
        setErrorMessage('This file is password protected');
        setIsError(true);
        break;

      default:
        if (isStarted) {
          console.log('unexpected error in mapping', fileState);
          setErrorMessage('Unexpected error occurred');
          setIsError(true);
        }
        break;
    }
  }, [fileState, setIsStarted, isStarted, processingPercent]);

  const reset = () => {
    setIsStarted(false);
    setIsError(false);
  };

  return (
    <MapFieldsOperation
      isStarted={isStarted}
      setIsStarted={setIsStarted}
      progress={progress}
      description={description}
      subDescription={subDescription}
      buttonText={buttonText}
      isError={isError}
      errorMessage={errorMessage}
      reset={reset}
    />
  );
};

const MapFieldsOperationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { selectedFile, setSelectedFileId } = useTapeUploadContext();
  const { mapHeadersToFields } = useMapHeadersToFields();
  const [isStarted, setIsStarted] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [description, setDescription] = useState<string>('Initializing...');
  const [subDescription, setSubDescription] = useState<string | undefined>(undefined);
  const [buttonText, setButtonText] = useState<string>('Cancel');
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('An error occurred');

  useEffect(() => {
    if (selectedFile) {
      switch (selectedFile.state) {
        case FileState.NEEDS_MAPPING:
          setProgress(0);
          setDescription('Starting...');
          setSubDescription(undefined);
          setButtonText('Cancel');
          break;

        case FileState.READY_TO_PROCESS:
          setProgress(0);
          setDescription('Mapping fields');
          setSubDescription(undefined);
          setButtonText('Cancel');
          break;

        case FileState.PROCESSING:
          const processingPercent = selectedFile.processing_percent ?? 0;
          setProgress(processingPercent);
          setDescription('Processing loans...');
          setSubDescription('You can close this and come back later.');
          setButtonText('OK');
          break;

        case FileState.PROCESSED:
          setProgress(100);
          setDescription('Fields mapped.');
          setSubDescription(undefined);
          setButtonText('OK');
          setIsStarted(false);
          break;

        case FileState.ERROR:
          setErrorMessage('An error occurred while mapping fields');
          setIsError(true);
          break;

        case FileState.ERROR_PASSWORD:
          setErrorMessage('This file is password protected');
          setIsError(true);
          break;

        default:
          if (isStarted) {
            console.log('unexpected error in mapping', selectedFile.state);
            setErrorMessage('An unexpected error occurred');
            setIsError(true);
          }
          break;
      }
    }
  }, [isStarted, selectedFile]);

  const startMapFields = async (fileId: string, fieldMap: FieldsMap) => {
    setIsStarted(true);
    await mapHeadersToFields(fileId, fieldMap);
  };

  const reset = () => {
    setSelectedFileId(undefined);
    setIsStarted(false);
    setIsError(false);
  };

  return (
    <MapFieldsOperationContext.Provider value={{ startMapFields }}>
      {children}
      <MapFieldsOperation
        isStarted={isStarted}
        setIsStarted={setIsStarted}
        progress={progress}
        description={description}
        subDescription={subDescription}
        buttonText={buttonText}
        isError={isError}
        errorMessage={errorMessage}
        reset={reset}
      />
    </MapFieldsOperationContext.Provider>
  );
};

const useMapFieldsOperation = () => {
  const context = useContext(MapFieldsOperationContext);
  if (!context) {
    throw new Error('useMapFieldsOperation must be used within MapFieldsOperationProvider');
  }
  return context;
};

const MapFieldsOperation: React.FC<{
  isStarted: boolean;
  setIsStarted: React.Dispatch<React.SetStateAction<boolean>>;
  progress: number;
  description: string;
  subDescription?: string;
  buttonText: string;
  isError: boolean;
  errorMessage: string;
  reset: () => void;
}> = ({
  isStarted,
  setIsStarted,
  progress,
  description,
  subDescription,
  buttonText,
  isError,
  reset,
  errorMessage,
}) => {
  return (
    <>
      {!isError && (
        <InProgressOverlay
          title="Mapping In Progress"
          description={description}
          subDescription={subDescription}
          open={isStarted}
          cancel={() => setIsStarted(false)}
          onSuccessClicked={() => setIsStarted(false)}
          progress={progress}
          cancelButtonText={buttonText}
        />
      )}
      {isError && <ErrorDialog onErrorDialogClose={reset} errorMessage={errorMessage} />}
    </>
  );
};

interface ErrorDialogProps {
  onErrorDialogClose: () => void;
  errorMessage: string;
}

const ErrorDialog = ({ onErrorDialogClose, errorMessage }: ErrorDialogProps) => {
  const dialog = useDialog();
  useEffect(() => {
    dialog.setIsOpen(true);
  }, [dialog]);
  return (
    <Dialog dialog={dialog}>
      <div className="mt-[50px] text-center text-[24px] font-medium leading-[36px] text-white">
        Upload Error
      </div>
      <div>{errorMessage}</div>
      <DialogActions>
        <Button size="medium" label="cancel" onClick={onErrorDialogClose}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { MapFieldsOperationProvider, useMapFieldsOperation };
